// Local draft persistence for the Invite modal.
//
// The invite grid used to live only in component state, so closing the modal
// (or the app reloading) silently discarded everything typed — a facilitator
// lost 17 hand-entered rows this way on launch day. The modal now writes its
// state here on every change and restores it the next time it opens.

export interface InviteDraftRow {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  snapshotType: string;
  forumId: number | '';
  customAmount: string;
  guestExpiresAt: string;
  skipOnboarding: boolean;
}

export interface InviteDraft {
  savedAt: number;
  rows: InviteDraftRow[];
  personalNote: string;
  scheduleEnabled: boolean;
  sendAt: string;
}

const DRAFT_KEY = 'if-invite-draft-v1';

// Old enough that a restored draft is stale noise rather than a save.
export const INVITE_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export function draftRowHasContent(row: InviteDraftRow): boolean {
  return Boolean(row.firstName.trim() || row.lastName.trim() || row.email.trim());
}

export function loadInviteDraft(now: number = Date.now()): InviteDraft | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(DRAFT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const draft = JSON.parse(raw) as InviteDraft;
    if (
      !draft ||
      typeof draft.savedAt !== 'number' ||
      !Array.isArray(draft.rows) ||
      !draft.rows.some(draftRowHasContent)
    ) {
      clearInviteDraft();
      return null;
    }
    if (now - draft.savedAt > INVITE_DRAFT_TTL_MS) {
      clearInviteDraft();
      return null;
    }
    return draft;
  } catch {
    clearInviteDraft();
    return null;
  }
}

export function saveInviteDraft(
  draft: Omit<InviteDraft, 'savedAt'>,
  now: number = Date.now(),
): void {
  try {
    // A draft is only worth keeping if at least one person has been typed in;
    // a personal note alone isn't recoverable work.
    if (!draft.rows.some(draftRowHasContent)) {
      localStorage.removeItem(DRAFT_KEY);
      return;
    }
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...draft, savedAt: now }));
  } catch {
    // Storage unavailable/full — the draft is best-effort, never blocking.
  }
}

export function clearInviteDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    // Ignore: nothing to clean up if storage is unavailable.
  }
}
